import React, { useEffect, useState } from 'react';
import { formattedCurrency } from '../../../utils/StringUtils';
import { TableBody, TableHead, TableRow, CircularProgress, Box, Table, Stack, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { ISTableHeaderCell, ISTableRow, ISTableCell } from '../../Utils/ISTable';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../theme/ThemeContext';
import { read, utils } from 'xlsx';
import Goal from '../../../models/Goal';
import { PersonType, personTypeToDescription } from '../../Client/Profile';
import ProductBrand from '../../../models/ProductBrand';
import { DesktopDatePicker } from '@mui/lab'
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import AdapterMoment from '@mui/lab/AdapterMoment'
import moment from 'moment';
import Selector from '../../Selector';
import { GOAL_KIND_STANDARD, translateTypeOfGoalDescription } from './GoalList';
import { getToken } from '../../../utils/fetchApi';

interface XlsImportGoalProps {
  isOpen: boolean
  brands: ProductBrand[]
  onConfirm: (value: Goal[]) => void
  onCancel: () => void
}

export const XlsImportGoalDialog: React.FC<XlsImportGoalProps> = (props) => {
  const { t } = useTranslation(['goal', 'menu', 'others'])
  const { theme } = useThemeContext()
  const [xlsGoal, setXlsGoal] = useState<any>([]);
  const [goalsToImport, setGoalsToImport] = useState<Goal[]>([]);
  const fileInput = React.useRef<HTMLInputElement>(null);
  const [isBulkingData, setIsBulkingData] = useState<boolean>(false)

  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const [typeOfGoal, setTypeOfGoal] = useState<string>('');
  const [personType, setPersonType] = useState<PersonType | ''>('');
  const [minPriceListId, setMinPriceListId] = useState<number>(0);
  const [maxPriceListId, setMaxPriceListId] = useState<number>(9999);

  const handleXlsImportGoal = async (file: File) => {
    const f = await file.arrayBuffer();
    const wb = read(f); // parse the array buffer
    const ws = wb.Sheets[wb.SheetNames[0]]; // get the first worksheet
    const data = utils.sheet_to_json(ws); // generate objects
    setXlsGoal(data.map((d: any) => (d)))
  };

  const searchBrandByName = (name: string) => {
    if (!name || name.toUpperCase() === 'GENERAL' || name.toUpperCase() === 'TOTAL') return null
    if (name.toUpperCase() === 'RESTO') return 9999
    return props.brands.find(brand => brand.description === name.toUpperCase())?.id || null
  }

  useEffect(() => {
    if (xlsGoal && from && to && typeOfGoal && personType) {
      let newGoalsToImport: Goal[] = []

      xlsGoal.forEach(xlsItem => {
        newGoalsToImport.push(
          {
            id: null,
            from: from,
            to: to,
            typeOfGoal: typeOfGoal,
            minPriceListId: minPriceListId,
            maxPriceListId: maxPriceListId,
            personType: personType,
            byPersonTypeId: xlsItem[Object.keys(xlsItem)[0]],
            brandId: searchBrandByName(xlsItem[Object.keys(xlsItem)[1]]),
            goal: xlsItem[Object.keys(xlsItem)[2]],
            brandName: xlsItem[Object.keys(xlsItem)[1]],
            dataAdic: xlsItem[Object.keys(xlsItem)[3]] || '',
            currency: '',
            status: 'ACTIVE',
            goalKind: GOAL_KIND_STANDARD,
            ownerId: 0,
            ownerName: '',
            updated: null
          })
      })
      setGoalsToImport(newGoalsToImport)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps    
  }, [xlsGoal, from, to, typeOfGoal, personType, minPriceListId, maxPriceListId])

  const setAttachedFileInfo = async () => {
    const files = (fileInput as any).current.files as File[];
    if (files.length && files[0]) {
      handleXlsImportGoal(files[0])
    }
  }

  const translatePersonTypeDescription = (value: string): string => t(personTypeToDescription(value), { ns: 'person' })

  const handleClose = (
    event: {},
    reason: "backdropClick" | "escapeKeyDown"
  ) => {
    if (reason !== "backdropClick") {
      setIsBulkingData(false);
      props.onCancel && props.onCancel();
    }
  };

  return (
    <>
      <Dialog
        open={props.isOpen}
        maxWidth="lg"
        disableRestoreFocus
        onClose={handleClose}
      >
        <DialogTitle id="draggable-dialog" sx={{ cursor: 'auto', fontWeight: 'bold', height: '50px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {t('import')}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: theme.palette.background.default }}>
          <Stack textAlign="center" padding={1}>
            <Stack direction={'row'}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DesktopDatePicker
                  label={t("from")}
                  inputFormat="DD/MM/yyyy"
                  renderInput={(params) => <TextField {...params} required sx={{ marginRight: "10px" }} />}
                  value={from ? moment(from) : null}
                  onChange={date => { if (date) { setFrom(moment(date).tz("America/Argentina/Buenos_Aires").format()) } }}
                />
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DesktopDatePicker
                  label={t("to")}
                  inputFormat="DD/MM/yyyy"
                  renderInput={(params) => <TextField {...params} required />}
                  value={to ? moment(to) : null}
                  onChange={date => { if (date) { setTo(moment(date).tz("America/Argentina/Buenos_Aires").format()) } }}
                />
              </LocalizationProvider>
            </Stack>
            <Stack direction={'row'}>
              <Selector
                key={"personType"}
                label={t("personType")}
                value={personType}
                menuItems={['C', 'V']}
                textAdapter={translatePersonTypeDescription}
                style={{ width: '100%', marginRight: "10px" }}
                onChange={(e) => setPersonType(e.target.value)}
                required />
              <Selector
                key={"typeOfGoal"}
                variant="outlined"
                label={t("typeOfGoal")}
                value={typeOfGoal}
                menuItems={['$', 'Qty']}
                textAdapter={translateTypeOfGoalDescription}
                style={{ width: '100%' }}
                onChange={(e) => setTypeOfGoal(e.target.value)}
                required
              />
            </Stack>
            <Stack direction={'row'}>
              <TextField
                label={t("minPriceListId")}
                type="number"
                value={minPriceListId}
                style={{ marginTop: '16px', width: '100%', marginRight: "10px"  }}
                onChange={(e) => setMinPriceListId(Number(e.target.value))}
              />
              <TextField
                label={t("maxPriceListId")}
                type="number"
                value={maxPriceListId}
                style={{ marginTop: '16px', width: '100%' }}
                onChange={(e) => setMaxPriceListId(Number(e.target.value))}
              />
            </Stack>

            <Box style={{ justifyContent: 'center' }}>
              <TextField
                InputLabelProps={{ shrink: true }}
                inputProps={{ accept: "application/xlsx" }}
                inputRef={fileInput}
                label={t('loadFile')}
                margin="normal"
                variant="outlined"
                onChange={() => setAttachedFileInfo()}
                type="file"
                fullWidth
                disabled={xlsGoal && xlsGoal.length > 0}
              />
            </Box>
            {xlsGoal && xlsGoal.length > 0 &&
              <Box style={{ justifyContent: 'center' }}>
                <Table size="medium" stickyHeader={true}>
                  <TableHead >
                    <TableRow selected={true}>
                      <ISTableHeaderCell>{t('byPersonTypeId')}</ISTableHeaderCell>
                      <ISTableHeaderCell>{t('brandName')}</ISTableHeaderCell>
                      <ISTableHeaderCell align='right'>{t('goal')}</ISTableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {xlsGoal && xlsGoal.map((row, index) => {
                      return <ISTableRow key={index} color='default'
                        sx={{ lineHeight: "0.5rem", fontSize: "12px" }}>
                        <ISTableCell>
                          {row[Object.keys(row)[0]]!}
                        </ISTableCell>
                        <ISTableCell>
                          {row[Object.keys(row)[1]]!}
                        </ISTableCell>
                        <ISTableCell align='right'>
                          {formattedCurrency(row[Object.keys(row)[2]]!)}
                        </ISTableCell>
                      </ISTableRow>
                    })}
                  </TableBody>
                </Table>
              </Box>}
          </Stack>

          {
            (isBulkingData) &&
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={25} />
            </div>
          }
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', backgroundColor: theme.palette.background.default }}>
          <Box>
            <Button aria-label="download" color="secondary" variant="contained" size='small' style={{ textTransform: 'none' }}
                href={`${process.env.REACT_APP_API_URL}/goals-stats/template?access_token=${getToken()}`} target="_blank">
                &nbsp;{`${t('download', { ns: 'others' })} ${t('template')}`}&nbsp;
              </Button>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
            {props.onCancel && <Button style={{ marginRight: '8px', textTransform: 'none' }} color="error" variant="contained" onClick={() => props.onCancel && props.onCancel()}>{t('cancel')}</Button>}
            {props.onConfirm && <Button disabled={xlsGoal.length === 0 || isBulkingData} style={{ marginRight: '8px', textTransform: 'none' }} color="success" variant="contained"
              data-testid="generic-ok" onClick={() => { setIsBulkingData(true); props.onConfirm && props.onConfirm(goalsToImport) }}>{t('import')}</Button>}
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
}
